define('viewModels/widgets/CustomListEditor',[
    'lib/knockout',
    'lib/lodash4',
    'viewModels/ViewModel',
    'lib/bluebird',
    'util/errorCallbackFactory',
    'jquery',
    'util/keyCode'
], function (ko, _, ViewModel, Promise, errorCallbackFactory, $, keyCode) {
    'use strict';


    /**
     *  Creates view model needed by the add remove custom list widget
     *
     * # ARGUMENTS
     *
     * @param {Method} ObservableModelConstructorRef - Observable model reference
     * @param {Array} columns - An array of the column objects to create e.g. { label: 'en-US-key', cssClass: 'divider-class' }
     *    cssClass defaults to 'form-group' so if your columnn is a form group you can ignore this.
     * @param {Array} itemsObservableArray - An observable array to which content is to be saved into
     * @param {String} itemEntryTemplate - Template name,
     * @param {Object} serviceControls - Service controls object in the form of:
     *
     * serviceControls: {
     *      create: {
     *          service - {Method} Async service method responsible for creating an item. This widget will handle errors by
     *              toasting, but it will check that a response/error exists in case the VM handles the error.
     *          namespace: {String} Namespace string used primarily during error callbacks
     *      },
     *      update: {
     *          service - {Method} Async service method responsible for updating an item. This widget will handle errors by
     *              toasting, but it will check that a response/error exists in case the VM handles the error.
     *          disableAllUpdates: {boolean} - Boolean to control if other update controls should be disabled
     *              during an update of the current item.
     *          namespace: {String} Namespace string used primarily during error callbacks
     *
     *      },
     *      remove: {
     *          service - {Method} Async service method responsible for removing an item.
     *          disableAllRemoves: {boolean} - Boolean to control if other remove controls should be disabled
     *              during an removal of the current item.
     *          namespace: {String} Namespace string used primarily during error callbacks
     *          hideRemove: {Method} Method that determines whether an item's hide button should be removed
     *      }
     * }
     * @param {Object} miscControls - Other widget controls:
     *
     * miscControls: {
     *    itemListTemplate - {String} Template name for item list row (if not provided, 'itemEntryTemplate' used for both.
     *    placeholder - {String} Localized string that will be used when the list is empty
     *    minHeight - {String} - CSS value for minimum height of the container (e.g. '10%', '200px')
     *    maxHeight - {String} - CSS value for maximum height of the container (e.g. '10%', '200px')
     *    isModelDirty - {Method} - Method that determines if the passed in model is dirty or not. Takes in an observable model
     *    inheritedFormControlObj - {Object} - Inherited form control object
     *    enableCreate - {Boolean} - Boolean observable to control when to allow creation of the new item
     *    readOnly - {observable boolean} - observable or computed boolean that determines whether to disable editing of list.
     *                                      If true this will remove the new item field and add/remove/save buttons. This will not
     *                                      disable the provided template.
     * }
     *
     */
    var CustomListEditor = ViewModel.extend(function (ObservableModelConstructorRef, columns, itemsObservableArray, itemEntryTemplate, serviceControls, miscControls) {
        var self = this;
        var ADDED_ITEMS_CLASS = ".added-items";
        var ADDED_ITEM_CLASS = ".added-item";
        var NEW_ITEM_CLASS = '.new-item';
        var COLUMN_HEADINGS_CLASS = '.column-headings';
        var CUSTOM_LIST_EDITOR_CLASS = ".custom-list-editor";
        var subscribeRef = null;
        var CONTAINER_DEFAULT_HEIGHT = '150px';
        var TIMEOUT_VALUE = 250;
        var EVENT_NAMESPACE = ".custom-list-editor";
        var CONTROL_SELECTORS = "input, select";
        var customListEditorNode;
        var newItemFormGroups = [];

        var focusFirstNewItemInput = function () {
           _.delay(function () {
                var inputs = $(CONTROL_SELECTORS, $(NEW_ITEM_CLASS, customListEditorNode));
                //focus the first input
                if (inputs.length > 0) {
                    inputs[0].focus();
                }
           }, TIMEOUT_VALUE);
        };
        self.newItem = ko.observable();
        self.entryTemplate = itemEntryTemplate;
        var items = itemsObservableArray;
        self.addedItems = ko.observableArray();

        if (miscControls && miscControls.isModelDirty) {
            self.isModelDirty = miscControls.isModelDirty;
        }

        self.newItemEnterKey = function (data, event) {
            if (event.which === keyCode.ENTER && self.enableAddItem()) {
                self.addItem();
            }
        };
        self.addedItemsEnterKey = function (data, event) {
            if (event.which === keyCode.ENTER && data.update.isUpdateVisible()) {
                self.updateItem(data);
            }
        };

        self.listTemplate = miscControls && miscControls.itemListTemplate || itemEntryTemplate;
        self.emptyContainerPlaceholder = miscControls && miscControls.placeholder || 'customListEditor.emptyPlaceholder';
        self.containerMinHeight = miscControls && miscControls.minHeight || CONTAINER_DEFAULT_HEIGHT;
        self.containerMaxHeight = miscControls && miscControls.maxHeight || CONTAINER_DEFAULT_HEIGHT;
        self.containerPlaceholderMaxHeight = self.containerMaxHeight.toLowerCase() === 'none' ? CONTAINER_DEFAULT_HEIGHT : self.containerMaxHeight;
        self.inheritedFormControlObj =  miscControls && miscControls.inheritedFormControlObj || null;
        self.enableCreate =  miscControls && miscControls.enableCreate || ko.observable(true);
        self.isSubscribingToListUpdates  = ko.observable(true);
        self.enableAddItem = self.computed(function(){
            return  self.newItem() && !self.newItem().hasErrors() && self.enableCreate();
        });
        self.readOnly = miscControls && miscControls.readOnly || ko.observable(false);
        self.isUpdateInProgress = ko.observable();
        self.isRemoveInProgress = ko.observable();

        if (self.inheritedFormControlObj) {
            self.inheritedFormControlObj.classToAdorn = 'inherited-form-control-placeholder';
        }

        // Setup some defaults on the columns object
        self.columns = _.map(columns, function (column) {
            // Originally we just had labels being passed in, so handle that
            if (_.isString(column)) {
                return { label: column };
            } else {
                return _.assign({ 'cssClass': 'form-group' }, column);
            }
        });

        // Inherit any validation from passed in items array to local added items array
        var validationRules = ko.observableArray([]);
        self.addedItems.extend({required:false}); //init validation
        self.addedItems.rules = self.computed({
            read: function() {
                return validationRules();
            },
            write: function(rules) {
                validationRules(rules);
            }
        });

        if (items && items.rules) {
            validationRules(items.rules());
        }

        //method responsible for creating a viewModel
        //that a template uses
        var createListItem = function (model) {
            var viewModel = {};
            viewModel.model = model || new ObservableModelConstructorRef();
            viewModel.listTemplate = self.listTemplate;

            viewModel.pristineObject = viewModel.model.unwrap();
            viewModel.updatePristineObject = function (model) {
                viewModel.pristineObject = model.unwrap();
            };

            viewModel.hasModelChanged = function () {
                if (self.isModelDirty) {
                    return self.isModelDirty(viewModel.model);
                }
                //perform basic dirty checking
                else {
                    return JSON.stringify(viewModel.model.unwrap()) !== JSON.stringify(viewModel.pristineObject);
                }
            };

            var getNamespacesForAction = function(actionPropertyName, defaultNamespace) {
                if(serviceControls && serviceControls[actionPropertyName]) {

                    var actionProperty = serviceControls[actionPropertyName];
                    if(actionProperty.namespaces) {
                        var namespaces = _.clone(actionProperty.namespaces);
                        namespaces.push(defaultNamespace);
                        return namespaces;
                    }

                    if(actionProperty.namespace) {
                        return [actionProperty.namespace, defaultNamespace];
                    }

                    return [defaultNamespace];
                }
            };

            var errors = ko.observableArray();

            // validation doesn't work in test cases
            if (ko.validation) {
                errors = ko.validation.group(viewModel.model, { deep: true, live: true });
            }

            viewModel.hasErrors = self.computed(function() { return errors().length > 0; });

            viewModel.create = {};
            viewModel.create.service = serviceControls && serviceControls.create && serviceControls.create.service ? serviceControls.create.service : null;
            viewModel.create.inProgress = ko.observable(false);
            viewModel.create.namespaces = getNamespacesForAction('create', 'customListEditor.add.error');

            viewModel.update = {};
            viewModel.update.service = serviceControls && serviceControls.update && serviceControls.update.service ? serviceControls.update.service : null;
            viewModel.update.inProgress = ko.observable(false);
            viewModel.update.namespaces = getNamespacesForAction('update', 'customListEditor.update.error');

            viewModel.update.isUpdateVisible = self.computed(function() {
                return  serviceControls &&
                        serviceControls.update &&
                        viewModel.hasModelChanged() &&
                        !viewModel.hasErrors() &&
                        (!serviceControls.update.disableAllUpdates || !self.isUpdateInProgress());
            });

            viewModel.remove = {};
            viewModel.remove.service = serviceControls && serviceControls.remove && serviceControls.remove.service ? serviceControls.remove.service : null;
            viewModel.remove.inProgress = ko.observable(false);
            viewModel.remove.namespaces = getNamespacesForAction('remove', 'customListEditor.remove.error');
            viewModel.remove.hideRemove = serviceControls && serviceControls.remove && serviceControls.remove.hideRemove ? serviceControls.remove.hideRemove : function() { return false; };
            viewModel.remove.isRemoveVisible = self.computed(function() {
                if (viewModel.remove.hideRemove(viewModel.model)) {
                    return false;
                }

                return !serviceControls ||
                       !serviceControls.remove ||
                       (!serviceControls.remove.disableAllRemoves || !self.isRemoveInProgress());
            });

            viewModel.isRemoveOrUpdateInProgress = self.computed(function () {
                return viewModel.remove.inProgress() || viewModel.update.inProgress();
            });

            return viewModel;

        };

        self.populateList = function (items) {
            var itemsSize = self.addedItems().length;

            _.forEach(items, function (item) {
                self.addedItems.push(createListItem(item));
            });

            for (var i = 0; i< itemsSize; i++) {
                self.addedItems.shift();
            }
        };

        //Subscription method responsible for setting up the initial
        //state of the viewModel
        var subscribeToListNotifications = function (items) {
            self.populateList(items);
        };

        //subscribe to list notifications so that we can render the view
        //correctly upon initialization
        subscribeRef = self.subscribe(items, subscribeToListNotifications);

        // Resets the new item viewModel to its default state
        self.resetNewItem = function () {
            self.newItem(createListItem());
        };

        self.adjustWidth = function() {
            if(customListEditorNode) {
                _.delay(function () {
                    var addedItemNode = $(ADDED_ITEM_CLASS, customListEditorNode);
                    var newItemNode = $(NEW_ITEM_CLASS, customListEditorNode);
                    var columnHeadingNode = $(COLUMN_HEADINGS_CLASS, customListEditorNode);

                    if (addedItemNode.length > 0) {
                        var addedItemWidth = $(addedItemNode[0]).width();
                        // Only resize if visible
                        if (addedItemWidth > 100) {
                            if (newItemNode.length > 0) {
                                $(newItemNode[0]).width(addedItemWidth);
                            }
                            if (columnHeadingNode.length > 0) {
                                $(columnHeadingNode[0]).width(addedItemWidth);
                            }
                        }
                    }
                }, TIMEOUT_VALUE);
            }
        };

        self.addItem = function () {
            //at this point the view is initialized and we can un-subscribe
            subscribeRef.dispose();

            if (self.isSubscribingToListUpdates()) {
                self.isSubscribingToListUpdates(false);
            }

            //invoke create service only if there are no validation errors
            if (validateNewItem()) {
                if (self.newItem().create.service) {

                    //set inProgress to true so that the spinner is shown
                    self.newItem().create.inProgress(true);

                    //invoke service
                    self.newItem().create.service(self.newItem().model).then(function (response) {
                        var newModel = new ObservableModelConstructorRef(self.newItem().model.unwrap());

                        var responseData = response && response.unwrap ? response.unwrap() : response;

                        if (responseData && ObservableModelConstructorRef.fill) {
                            ObservableModelConstructorRef.fill(newModel, responseData);
                        }

                        //keep the two lists in sync
                        self.addedItems.push(createListItem(newModel));
                        items.push(newModel);

                        self.newItem(createListItem());

                        //Scroll to the bottom of the widget after the item has been created
                        if (customListEditorNode) {
                            var firstParent = $(ADDED_ITEMS_CLASS, customListEditorNode);
                            if (firstParent.length > 0) {
                                firstParent.animate({
                                    scrollTop: firstParent[0].scrollHeight}, TIMEOUT_VALUE);
                            }
                        }
                        focusFirstNewItemInput();
                    }).catch(function (error) {
                        if (error) {
                            errorCallbackFactory.createCallback({ namespaces: self.newItem().create.namespaces })(error);
                        }
                    })
                    .finally(function () {
                        self.newItem().create.inProgress(false);
                    });
                } else {
                    var newModel = new ObservableModelConstructorRef(self.newItem().model.unwrap());
                    //keep the two lists in sync
                    self.addedItems.push(createListItem(newModel));
                    items.push(newModel);
                    var emptyModel = new ObservableModelConstructorRef();
                    self.newItem(createListItem(emptyModel));

                    //Scroll to the bottom of the widget after the item has been created
                    if (customListEditorNode) {
                        var firstParent = $(ADDED_ITEMS_CLASS, customListEditorNode);
                        if (firstParent.length > 0) {
                            firstParent.animate({
                                scrollTop: firstParent[0].scrollHeight}, TIMEOUT_VALUE);
                        }
                    }
                    focusFirstNewItemInput();
                }
            }
        };

        self.removeItem = function (item) {
            //add this point the view is initialized and we can un-subscribe
            subscribeRef.dispose();

            if (self.isSubscribingToListUpdates()) {
                self.isSubscribingToListUpdates(false);
            }

            if (item.remove.service) {
                item.remove.inProgress(true);
                self.isRemoveInProgress(true);
                item.remove.service(item.model).then(function () {
                    //keep the two lists in sync
                    self.addedItems.remove(item);
                    items.remove(item.model);
                }).catch(function (error) {
                    if (error) {
                        errorCallbackFactory.createCallback({ namespaces: self.newItem().remove.namespaces })(error);
                    }
                }).finally(function(){
                    item.remove.inProgress(false);
                    self.isRemoveInProgress(false);
                    self.adjustWidth();
                });
            }
            else {
                self.addedItems.remove(item);
                items.remove(item.model);
                self.adjustWidth();
            }
        };

        self.updateItem = function (item) {
            if (item.update.service) {
                self.isUpdateInProgress(true);
                item.update.inProgress(true);
                //invoke update service
                item.update.service(item.model).then(function () {
                    //update the pristine object
                    item.updatePristineObject(item.model);
                }).catch(function (error) {
                    if (error) {
                        errorCallbackFactory.createCallback({ namespaces: self.newItem().update.namespaces })(error);
                    }
                }).finally(function(){
                    item.update.inProgress(false);
                    self.isUpdateInProgress(false);
                });
            }
        };

        // Validates new item row and focuses any invalid fields. Return true if the row passes validation.
        var validateNewItem = function() {
            // Mark each form group as modified. This will cause the errors to display.
            _.forEach(newItemFormGroups, function (formGroup) {
                if (formGroup.observable.isModified) {
                    formGroup.observable.isModified(true);
                }
            });

            // See if there is an invalid form group.
            var firstInvalidFormGroup = _.find(newItemFormGroups, function (formGroup) {
                if (formGroup.observable.isValid) {
                    return !formGroup.observable.isValid();
                }
                return false;
            });

            if (firstInvalidFormGroup) {
                if (firstInvalidFormGroup.controlElement) {
                    _.defer(function () { $(firstInvalidFormGroup.controlElement).focus(); });
                }
                return false;
            }

            return true;
        };

        self.postRenderingLogic = function (elements) {
            if (elements.length > 0) {
                // set reference to custom list editor node
                customListEditorNode = $(elements[0]).closest(CUSTOM_LIST_EDITOR_CLASS);

                // Ignore new item validation on save
                var form = $(customListEditorNode).closest('.pc-form, form');
                var newItemNode = $(NEW_ITEM_CLASS, customListEditorNode);
                var formGroups = $('.form-group', newItemNode);
                var formGroupList = ko.utils.domData.get(form[0], 'formGroupList');
                // Save reference to new item form groups
                newItemFormGroups = [];
                _.forEach(formGroups, function (formGroup) {
                    var foundEntry = _.find(formGroupList, function(groupListEntry){
                        return groupListEntry.groupElement === formGroup;
                    });

                    if(foundEntry) {
                        newItemFormGroups.push(foundEntry);
                    }
                });

                // Remove new item form groups from global list so they don't block save
                _.forEach(newItemFormGroups, function(groupListEntry){
                    var index = formGroupList.indexOf(groupListEntry);
                    if (index !== -1) {
                        formGroupList.splice(index, 1);
                    }
                });
            }
        };

        var preventDefaultPropagation = function(event) {
            if (event.target.closest(CUSTOM_LIST_EDITOR_CLASS) && event.which === keyCode.ENTER) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        self.onDispose = function() {
            $(window).off('resize' + EVENT_NAMESPACE, self.adjustWidth);
            $(window).off('keypress' + EVENT_NAMESPACE, preventDefaultPropagation);
        };

        self.populateList(items());
        //upon init create an empty model
        self.newItem(createListItem());

        //register event handlers
        $(window).on('resize' + EVENT_NAMESPACE, self.adjustWidth);
        //Prevent event propagation if the ENTER key is pressed within
        //customListEditor so that handler for adding a new item or
        //updating an existing item is properly invoked
        $(window).on('keypress' + EVENT_NAMESPACE, preventDefaultPropagation);
    });

    return CustomListEditor;
});

